<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader title="评估报告配置" subtitle="以下配置的内容将出现在主管提交评估后的《AI骨干人才保留建议报告》中，系统会根据员工的风险等级和行为特征自动匹配对应内容。" />

    <div class="toolbar">
      <el-tabs v-model="activeTab" class="content-tabs">
        <el-tab-pane label="管理建议" name="1" />
        <el-tab-pane label="成功案例" name="2" />
        <el-tab-pane label="推荐课程" name="3" />
        <el-tab-pane label="话术与FAQ" name="4" />
      </el-tabs>
      <el-button v-if="$hasPermission('content:create')" type="primary" icon="el-icon-plus" class="btn-click" @click="openDialog()">
        新增内容
      </el-button>
    </div>

    <div class="table-wrapper">
      <el-table :data="filteredContents" stripe class="data-table">
        <el-table-column prop="content_id" label="ID" width="70" />
        <el-table-column prop="title" label="标题" min-width="150" show-overflow-tooltip />
        <el-table-column prop="content" label="内容正文" min-width="200" show-overflow-tooltip />
        <el-table-column label="适用风险" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.risk_level === 0" size="mini">全部</el-tag>
            <el-tag v-else-if="scope.row.risk_level === 1" size="mini" type="success">低风险</el-tag>
            <el-tag v-else-if="scope.row.risk_level === 2" size="mini" type="warning">中风险</el-tag>
            <el-tag v-else size="mini" type="danger">高风险</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="关键词标签" min-width="150">
          <template slot-scope="scope">
            <el-tag v-for="(tag, i) in (scope.row.keywords || [])" :key="i" size="mini" type="info" style="margin-right: 4px; margin-bottom: 2px;">{{ tag }}</el-tag>
            <span v-if="!(scope.row.keywords && scope.row.keywords.length)" class="text-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column prop="sort_order" label="排序" width="70" />
        <el-table-column label="状态" width="80">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="small">
              {{ scope.row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150">
          <template slot-scope="scope">
            <el-button v-if="$hasPermission('content:edit')" type="text" class="action-edit" @click="openDialog(scope.row)">编辑</el-button>
            <el-button v-if="$hasPermission('content:delete')" type="text" class="action-disable" @click="deleteContent(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog :title="dialog.isEdit ? '编辑内容' : '新增内容'" :visible.sync="dialog.visible" width="560px" :append-to-body="true">
      <el-form :model="dialog.form" label-width="90px">
        <el-form-item label="标题" required>
          <el-input v-model="dialog.form.title" placeholder="请输入标题" />
        </el-form-item>
        <el-form-item label="内容正文" required>
          <el-input type="textarea" v-model="dialog.form.content" :rows="4" placeholder="请输入内容正文" />
        </el-form-item>
        <el-form-item label="适用风险">
          <el-select v-model="dialog.form.risk_level" placeholder="选择风险等级" style="width: 100%;">
            <el-option label="全部适用" :value="0" />
            <el-option label="低风险" :value="1" />
            <el-option label="中风险" :value="2" />
            <el-option label="高风险" :value="3" />
          </el-select>
        </el-form-item>
        <el-form-item label="关联行为项">
          <el-select
            v-model="dialog.form.behavior_tags"
            multiple
            filterable
            placeholder="选择关联的行为项，员工命中时优先匹配本条"
            style="width: 100%;"
          >
            <el-option
              v-for="b in allBehaviors"
              :key="b.behavior_id"
              :label="b.description"
              :value="b.behavior_id"
            />
          </el-select>
          <div class="form-hint">当员工在评估中命中这些行为项时，本条内容将优先进入匹配池</div>
        </el-form-item>
        <el-form-item label="关键词标签">
          <el-select
            v-model="dialog.form.keywords"
            multiple
            filterable
            allow-create
            default-first-option
            placeholder="输入关键词标签，用于前台智能匹配"
            style="width: 100%;"
          >
            <el-option v-for="tag in presetKeywords" :key="tag" :label="tag" :value="tag" />
          </el-select>
          <div class="form-hint">前台将根据员工的行为项标签自动匹配相关内容</div>
        </el-form-item>
        <el-form-item label="排序号">
          <el-input-number v-model="dialog.form.sort_order" :min="1" :max="999" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="dialog.form.status" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <div class="form-bottom-hint">
          <i class="el-icon-info" style="color: var(--color-accent); margin-right: 4px;" />
          以上内容（关联行为项、关键词标签、适用风险等）均会提供给 AI，由 AI 根据员工实际情况进行精准匹配与筛选，最终呈现到报告中。
        </div>
      </el-form>
      <span slot="footer">
        <el-button @click="dialog.visible = false">取消</el-button>
        <el-button type="primary" @click="saveContent">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '../../components/PageHeader.vue'

export default {
  name: 'ContentManage',
  components: { PageHeader },
  data() {
    return {
      activeTab: '1',
      filterRisk: '',
      dialog: {
        visible: false,
        isEdit: false,
        form: { content_id: null, type: 1, title: '', content: '', risk_level: 0, keywords: [], behavior_tags: [], content_type: 1, sort_order: 1, status: 1 }
      },
      presetKeywords: ['被忽视', '无发展', '薪资偏低', '工作量大', '沟通少', '外部机会', '绩效下滑', '频繁请假', '晋升失败', '压力大']
    }
  },
  computed: {
    contents() {
      const taskId = this.$store.getters.currentTaskId
      const all = this.$store.state.reportContentConfigs.contents || []
      if (!taskId) return all
      return all.filter(c => c.task_id === taskId || !c.task_id)
    },
    filteredContents() {
      const type = parseInt(this.activeTab)
      return this.contents.filter(c => c.type === type).sort((a, b) => a.sort_order - b.sort_order)
    },
    allBehaviors() {
      return this.$store.state.behaviors.behaviors || []
    }
  },
  methods: {
    openDialog(row) {
      if (row) {
        this.dialog.isEdit = true
        this.dialog.form = {
          ...row,
          keywords: row.keywords || [],
          behavior_tags: row.behavior_tags || [],
          content_type: row.content_type || 1
        }
      } else {
        this.dialog.isEdit = false
        this.dialog.form = {
          content_id: null,
          type: parseInt(this.activeTab),
          title: '',
          content: '',
          risk_level: 0,
          keywords: [],
          behavior_tags: [],
          content_type: 1,
          sort_order: this.filteredContents.length + 1,
          status: 1,
          task_id: this.$store.getters.currentTaskId || null
        }
      }
      this.dialog.visible = true
    },
    async saveContent() {
      const form = this.dialog.form
      if (!form.title.trim()) {
        this.$message.error('标题不能为空')
        return
      }
      if (!form.content.trim()) {
        this.$message.error('内容正文不能为空')
        return
      }
      try {
        if (this.dialog.isEdit) {
          await this.$api.updateContent(form.content_id, { ...form })
          this.$store.commit('UPDATE_CONTENT', { ...form })
          this.$message.success('内容已更新')
        } else {
          const res = await this.$api.createContent({ ...form })
          this.$store.commit('ADD_CONTENT', { ...form, content_id: res.data.content_id })
          this.$message.success('内容已新增')
        }
        this.dialog.visible = false
      } catch (e) {
        this.$message.error('保存失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
      }
    },
    async deleteContent(row) {
      try {
        await this.$confirm(`确定删除 "${row.title}"？`, '提示', { type: 'warning' })
        await this.$api.deleteContent(row.content_id)
        this.$store.commit('DELETE_CONTENT', row.content_id)
        this.$message.success('已删除')
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('删除失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
        }
      }
    }
  }
}
</script>

<style scoped>
.toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--space-lg); }
.content-tabs >>> .el-tabs__header { margin-bottom: 0; }
.content-tabs >>> .el-tabs__nav-wrap::after { height: 1px; background-color: var(--color-border); }
.table-wrapper { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); overflow: hidden; box-shadow: var(--shadow-sm); }
.data-table >>> th { background: var(--color-bg) !important; color: var(--color-text) !important; font-weight: 600 !important; }
.action-edit { color: var(--color-primary) !important; }
.action-disable { color: var(--color-high) !important; }
.form-bottom-hint {
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.6;
  background: var(--color-bg);
  padding: 10px 12px;
  border-radius: var(--radius-md);
  margin-top: 4px;
}
</style>
